import React, { useState, useEffect } from "react";
import { ColumnsPhotoAlbum } from "react-photo-album";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

import "react-photo-album/columns.css";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import {
  getLatestPredictedPhotos,
  photoLike,
  getPhotoUrlBatch,
  getBatchPhotoLikeStatus
} from "../../services/photoService";
import LikeButton from "../../components/LikeButton";
import type { Photo } from "../../types/photo";
import { Utils } from "../../utilities/Utils";

interface PhotoWithUrl {
  photoId: number;
  src: string;
  width: number;
  height: number;
  title: string;
  uploadTime: string;
  uploader: string;
  liked: boolean;
  likeCount: number;
}

export default function PopularPhotoGallery() {
  const [photos, setPhotos] = useState<PhotoWithUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const width = window.innerWidth;
    let limit = 12;
    if (width < 640) limit = 6;
    else if (width < 1024) limit = 9;

    const fetchPhotos = async () => {
      try {
        const topPhotos: Photo[] = await getLatestPredictedPhotos(limit);
        const photoIds = topPhotos.map((p) => p.photoId);
        const urls = await getPhotoUrlBatch(photoIds);
        const statuses = await getBatchPhotoLikeStatus(photoIds);

        const formatted: PhotoWithUrl[] = topPhotos.map((p, i) => ({
          photoId: p.photoId,
          src: urls[p.photoId],
          width: p.imgWidth,
          height: p.imgHeight,
          title: p.title,
          uploadTime: Utils.formatDate(p.uploadTime),
          uploader: p.uploadedBy?.name ?? "Unknown",
          liked: statuses[i].liked,
          likeCount: statuses[i].likeCount,
        }));


        setPhotos(formatted);
      } catch (err) {
        console.error("Failed to fetch popular photos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const handleToggle = (photoId: number, liked: boolean, likeCount: number) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.photoId === photoId ? { ...p, liked, likeCount } : p
      )
    );
  };

  if (loading) {
    return (
      <section className="custom-section">
        <h1 className="text-2xl font-bold mb-4">New Photos</h1>
        <p className="text-gray-400">Loading...</p>
      </section>
    );
  }

  return (
    <section className="py-9">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
      {photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center text-gray-500 py-12 space-y-2">
          <span className="block text-3xl font-bold">😔 No Upcoming Photos</span>
          <span className="block">
            No photos match the criteria right now. Please check back later.
          </span>
        </div>
          ) : (
        <>
      <div>
        <h2 className="flex items-center text-3xl font-bold">
          Recent Popular Photos
        </h2>
        <p className="text-lg text-gray-600 mb-4">
          Photos gaining the most traction right now.
        </p>
      </div>
      <ColumnsPhotoAlbum
        photos={photos}
        onClick={({ index }) => setOpenIndex(index)}
        columns={(containerWidth) => {
          if (containerWidth < 640) return 2;
          if (containerWidth < 1024) return 3;
          return 4;
        }}
        spacing={8}
        componentsProps={{
          button: {
            className: "relative group cursor-pointer overflow-hidden",
          },
        }}
        render={{
          extras: (_props, { photo, index }) => (
            <div className="absolute bottom-0 left-0 right-0 h-[45%] bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-2">
              <div className="flex flex-col text-white text-left">
                <div className="text-sm font-semibold">{photo.title}</div>
                <div className="text-xs">{photo.uploadTime}</div>
                <div className="text-xs italic">By {photo.uploader}</div>
              </div>
              <LikeButton
                photoId={photo.photoId}
                initialLiked={photo.liked}
                initialLikeCount={photo.likeCount}
                size="text-sm"
                iconSize="w-4 h-4"
                customTextColor="text-white hover:text-red-500"
                onLikeAction={async (id) => {
                  const result = await photoLike(id);
                  handleToggle(id, result.liked, result.likeCount);
                  return result;
                }}
              />
            </div>
          ),
        }}
      />
        <Lightbox
          open={openIndex !== null}
          close={() => setOpenIndex(null)}
          index={openIndex ?? 0}
          slides={photos.map(p => ({ src: p.src, alt: p.title }))}
          plugins={[Thumbnails]}
          thumbnails={{
            position: "bottom",
            width: 120,
            height: 80,
            padding: 4,
            gap: 8,
            border: 2,
            borderStyle: "solid",
            borderColor: "rgba(255, 255, 255, 0.3)",
            borderRadius: 4,
        }}
      />
    </>
  )}
  </div>
    </section>
  );
}
